/* Import Local Modules */
import { Validator } from '@leverege/validator'

/**
 * Checks if a value is null or an empty string.
 */
function isNullOrEmpty( value ) {
  return value == null || value === ''
}

/**
 * Email validator.
 */
function email( email, options ) {
  const { isRequired = true } = options || {}
  if ( isRequired && isNullOrEmpty( email ) ) {
    return 'Email is required.'
  }
  if ( !isNullOrEmpty( email ) && !Validator.isEmail( email ) ) {
    return 'Email is invalid.'
  }
  return null
}

/**
 * Name validator. 
 */
function name( name, options ) {
  const { isRequired = true } = options || {}
  if ( isRequired && isNullOrEmpty( name ) ) {
    return 'Name is required.'
  }
  if ( !isNullOrEmpty( name ) && name.length < 2 ) {
    return 'Name must be at least 2 characters.'
  }
  return null
}

/**
 * Phone number validator. 
 */
function phone( phone, options ) {
  const { isRequired = true } = options || {}
  if ( isRequired && isNullOrEmpty( phone ) ) {
    return 'Phone Number is required.'
  }
  if ( !isNullOrEmpty( phone ) && !Validator.isPhone( phone ) ) {
    return 'Phone Number is invalid.'
  }
  return null
}

/**
 * Username validator. 
 */
function username( username, options ) {
  const { isRequired = true } = options || {}
  if ( isRequired && isNullOrEmpty( username ) ) {
    return 'Username is required.'
  }
  if ( !isNullOrEmpty( username ) && username.length < 4 ) {
    return 'Username must be at least 4 characters.'
  }
  return null
}

/**
 * Password validator. TODO: Add options for password strength.
 */
function password( password, options ) {
  const { isRequired = true } = options || {}
  if ( isRequired && isNullOrEmpty( password ) ) {
    return 'Password is required.'
  }
  if ( !isNullOrEmpty( password ) && password.length < 8 ) {
    return 'Password must be at least 8 characters.'
  }
  return null
}

/**
 * Confirm password validator.
 */
function confirmPassword( password, confirmPassword, options ) {
  const { isRequired = true, waitForPassword = false } = options || {}
  if ( isRequired && isNullOrEmpty( confirmPassword ) ) {
    return 'Password confirmation is required.'
  }
  const waitedForPassword = waitForPassword ? password != null && confirmPassword?.length >= password?.length : true
  if ( !isNullOrEmpty( confirmPassword ) && waitedForPassword && password !== confirmPassword ) {
    return 'Password does not match.'
  }
  return null
}

/**
 * OIDC client id validator. 
 */
function oidcClientId( oidcClientId ) {
  if ( oidcClientId == null || oidcClientId === '' ) {
    return 'OIDC Client ID is required.'
  }
  return null
}

/**
 * Role validator.
 */
function roles( roles, options ) {
  const { isRequired = true } = options || {}
  if ( isRequired && roles == null ) {
    return 'Roles is required.'
  }
  if ( roles != null && !Array.isArray( roles ) ) {
    return 'Roles must be an array.'
  }
  if ( roles != null && roles.length < 1 ) {
    return 'At least one role is required.'
  }
  return null
}

/**
 * Invite validator.
 */
function invite( data ) {
  return (
    email( data?.user?.email ) ||
    // Intentionally not validating password here in order to use PasswordValidator plugins
    // password( data?.user?.password ) ||
    roles( data?.roles )
  )
}

/**
 * Create validator.
 */
function create( data ) {
  return (
    email( data?.user?.email ) ||
    name( data?.user?.name ) ||
    phone( data?.user?.phone, { isRequired : false } ) ||
    username( data?.user?.username ) ||
    // Intentionally not validating password here in order to use PasswordValidator plugins
    // password( data?.user?.password ) ||
    confirmPassword( data?.user?.password, data?.user?.confirmPassword ) ||
    roles( data?.roles )
  )
}

/**
 * Activate validator.
 */
function activate( user ) {
  return (
    email( user?.email ) ||
    name( user?.name ) ||
    phone( user?.phone, { isRequired : false } ) ||
    username( user?.username ) ||
    // Intentionally not validating password here in order to use PasswordValidator plugins
    // password( user?.password ) ||
    confirmPassword( user?.password, user?.confirmPassword )
  )
}

/**
 * OIDC access validator.
 */
function oidcAccess( data ) {
  return oidcClientId( data?.user?.oidcClientId ) || roles( data?.roles ) 
}

/* Export Modules */
export default {
  email,
  name,
  phone,
  username,
  password,
  confirmPassword,
  oidcClientId,
  roles,
  invite,
  create,
  activate,
  oidcAccess
}
